import { Role } from "../domain/Role";
import { UserRoleContext } from "../providers/UserProvider";
import { ACCESS_TYPES, ATTRIBUTE_ROLES } from "../constants/constants";

export type ScopedAttribute = {
    [key: string]: {
        highestScope: "All" | "Product" | "Instance" | "Project";
        // allScopes is stored as string: "{ name: string; value: string }"
        // use JSON.parse() to get back proper object
        allScopes: Set<string>;
    };
};

export const findHighestScope = (scopes?: { name: string; value: string }[], scope?: string): string | undefined => {
    if (!scopes) return scope;
    if (scopes?.length === 0 || scope === "All") return "All";

    const scopeTypes: {
        [key: string]: {
            priority: number;
            scopedAccess: string;
        };
    } = {
        ProductId: {
            priority: 1,
            scopedAccess: "Product"
        },
        InstanceId: {
            priority: 2,
            scopedAccess: "Instance"
        },
        AttributeId: {
            priority: 3,
            scopedAccess: "Project"
        }
    };
    let highestScope = "None";

    for (let scope of scopes) {
        const { name } = scope;
        if (highestScope === "None") {
            highestScope = name;
        } else {
            if (scopeTypes[highestScope]?.priority > scopeTypes[name]?.priority) {
                highestScope = name;
            }
        }
    }

    if (!!scope) {
        for (let key in scopeTypes) {
            if (scopeTypes[key].scopedAccess === scope) scope = key;
        }
        highestScope = scopeTypes[highestScope]?.priority > scopeTypes[scope]?.priority ? scope : highestScope;
    }

    return scopeTypes[highestScope]?.scopedAccess;
};

export const getUserScopedAttributes = (adminCenterRoles: Role[]): ScopedAttribute => {
    return adminCenterRoles.reduce((scopedAttributes: any, role: Role) => {
        // `Role.attributes` is built with an optional chain in the REST model, so a role can arrive
        // without any attributes at all — iterating that directly throws.
        const attributes = role.attributes || [];
        for (let attribute of attributes) {
            const { key, scopes } = attribute;

            if (scopedAttributes[key]) {
                const { highestScope, allScopes } = scopedAttributes[key];

                scopes?.length === 0
                    ? allScopes.add("[]")
                    : scopes?.forEach((scope) => allScopes.add(JSON.stringify(scope)));

                scopedAttributes[key] = {
                    highestScope: findHighestScope(scopes, highestScope),
                    allScopes
                };
            } else {
                const newScopes = new Set();

                scopes?.length === 0
                    ? newScopes.add("[]")
                    : scopes?.forEach((scope) => newScopes.add(JSON.stringify(scope)));

                scopedAttributes[key] = {
                    highestScope: findHighestScope(scopes),
                    allScopes: newScopes
                };
            }
        }
        return scopedAttributes;
    }, {});
};

/**
 * A user counts as a super admin when their access is unrestricted — i.e. the "Read Products"
 * attribute carries no scopes, so `findHighestScope` resolves it to `ACCESS_TYPES.SUPER_ADMIN`.
 * Anything narrower (Product/Instance/Project) is a scoped admin.
 */
export const hasSuperAdminProductAccess = (scopedAttributes: ScopedAttribute): boolean =>
    scopedAttributes[ATTRIBUTE_ROLES.PRODUCTS.READ]?.highestScope === ACCESS_TYPES.SUPER_ADMIN;

/**
 * Convenience wrapper over `hasSuperAdminProductAccess` for callers that hold roles rather than
 * already-folded scoped attributes (e.g. checking each organization in a user's instance
 * permissions). Pass the roles from the org's Admin Center instance permission.
 */
export const isSuperAdmin = (roles: Role[]): boolean => hasSuperAdminProductAccess(getUserScopedAttributes(roles));

export const hasScopedAccess = (highestScope: string, scopedAccess: string): boolean => {
    const scopeLevels = ["Project", "Instance", "Product", "All"];

    return scopeLevels.indexOf(highestScope) >= scopeLevels.indexOf(scopedAccess);
};

export const allowScope = ({
    scope,
    context,
    email
}: {
    scope: { name: string; value: string };
    context: UserRoleContext;
    email: string | undefined;
}): boolean => {
    const contextKey = Object.keys(context).find((key) => key.toLowerCase() === scope.name?.toLowerCase()) || "";

    if (scope.value === "self") {
        return context[contextKey] === email;
    }

    return scope.value === context[contextKey];
};
